import { React, useState, useEffect } from "react";
import {
  TableContainer,
  TableCaption,
  Table,
  Thead,
  Tr,
  Td,
  Th,
  Tbody,
  Button,
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionButton,
  Box,
  AccordionIcon,
  Skeleton,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getPendingBookings,
  changeBookingStatus,
} from "../../services/bookings";
import { useWindowSize } from "../../utils/customHooks";
import BookingDetailsModal from "./BookingDetailsModal";
import { formatDateTimeCET } from "../../utils/helpers";
import AdminBookingSourceCell, {
  adminBookingRowBackground,
} from "../../components/admin-booking-source/AdminBookingSourceCell";

function PendingBookings() {
  const { t } = useTranslation("common");
  const toast = useToast();
  const { width } = useWindowSize();
  const [currentPage, setCurrentPage] = useState(1);
  const [isAppending, setIsAppending] = useState(false);
  const [fetchNew, setFetchNew] = useState(false);
  const [bookingsData, setBookingsData] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [mutatingBookingId, setMutatingBookingId] = useState(null);

  //const [clickedIndex, setClickedIndex] = useState(null);

  /*const handleClick = (index) => {
    setClickedIndex(index);
    setTimeout(() => {
      setClickedIndex(null);
    }, 2000);
  };
  */

  const { data, isSuccess, isFetching, refetch } = useQuery(
    ["pendingBookings", currentPage],
    () =>
      getPendingBookings({
        page: !fetchNew ? currentPage : 1,
      }),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      select: (data) => data.data,
      onSuccess: (data) => {
        // Prepend new data to the top
        const existingIds = new Set(bookingsData.map((booking) => booking.id));
        const filteredNewData = data.results.filter(
          (booking) => !existingIds.has(booking.id)
        );
        if (isAppending) {
          // Append new data to the end
          setBookingsData((prevBookings) => [
            ...prevBookings,
            ...filteredNewData,
          ]);
        } else {
          setBookingsData((prevBookings) => [
            ...filteredNewData,
            ...prevBookings,
          ]);
        }

        // Reset appending and fetchNew flags
        setFetchNew(false);
        setIsAppending(false);
      },
      enabled: true,
    }
  );

  useEffect(() => {
    if (data?.pagination.nextPage && data?.pagination.totalPages > currentPage)
      setHasNextPage(true);
    else setHasNextPage(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (fetchNew) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNew]);

  const bookingStatusUpdateMutation = useMutation({
    mutationFn: (booking) => {
      setMutatingBookingId(booking.id);
      return changeBookingStatus(booking.id, booking);
    },
    onError: (error) => {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to update booking status";
      toast({
        title: "Update failed",
        description: message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
      setMutatingBookingId(null);
    },
    onSuccess: (data, variables) => {
      setBookingsData((prevBookings) =>
        prevBookings.filter((booking) => booking.id !== data.data.id)
      );
      setCurrentPage(currentPage - 1);
      toast({
        title:
          variables.currentStatus === "approved"
            ? "Reservation approved"
            : "Reservation declined",
        description:
          variables.currentStatus === "approved"
            ? data.data.isAffiliateReservation
              ? "The reservation has been approved."
              : "PayPal deposit captured successfully."
            : "The reservation has been declined.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      setMutatingBookingId(null);
    },
  });

  if (isSuccess) {
    if (width > 768) {
      return (
        <>
          <Button isLoading={fetchNew} onClick={() => setFetchNew(true)}>
            Refetch
          </Button>
          <TableContainer overflowX="unset" overflowY="unset">
            <Table variant="simple">
              <TableCaption>{t("pendingReservations")}</TableCaption>
              <Thead position="sticky" top={0}>
                <Tr>
                  <Th
                    backgroundColor="lightcoral"
                    position={"sticky"}
                    left={"0"}
                  >
                    Id
                  </Th>
                  <Th>{t("adminBookedBy")}</Th>
                  <Th>{t("adminTraveler")} name</Th>
                  <Th>{t("adminTraveler")} surname</Th>
                  <Th>Guest telephone number</Th>
                  <Th>E-mail</Th>
                  <Th>Start date</Th>
                  <Th>End date</Th>
                  <Th>Price</Th>
                  <Th>Reservation type</Th>
                  <Th>Driver included</Th>
                  <Th>Booking details</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {fetchNew && (
                  <>
                    <Tr>
                      <Td colSpan="100%">
                        <Skeleton height="20px" />
                      </Td>
                    </Tr>
                    <Tr>
                      <Td colSpan="100%">
                        <Skeleton height="20px" />
                      </Td>
                    </Tr>
                  </>
                )}
                {bookingsData.map((booking, index) => (
                  <Tr key={booking.id} bg={adminBookingRowBackground(booking)}>
                    <Td
                      backgroundColor="lightcoral"
                      position={"sticky"}
                      left={"0"}
                    >
                      <strong>{booking.referenceNumber}</strong>
                    </Td>
                    <Td>
                      <AdminBookingSourceCell booking={booking} />
                    </Td>
                    <Td>{booking.client?.firstName}</Td>
                    <Td>{booking.client?.lastName}</Td>
                    <Td>{booking.client?.telephoneNumber}</Td>
                    <Td>{booking.client?.email}</Td>
                    <Td>{formatDateTimeCET(booking.checkInDate)}</Td>
                    <Td>{formatDateTimeCET(booking.checkOutDate)}</Td>
                    <Td>{booking.fullPrice}</Td>
                    <Td>
                      {booking.accommodation
                        ? "stay"
                        : booking.car
                        ? "car"
                        : "tour"}
                    </Td>
                    <Td>{booking.driverIncluded ? "Yes" : "No"}</Td>
                    {booking.bookingType === "tour" ||
                    booking.bookingType === "stay" ? (
                      <Td>
                        <BookingDetailsModal
                          tour={booking.tour}
                          accommodationUnits={booking.accommodationUnits}
                          accommodation={booking.accommodation}
                        />
                      </Td>
                    ) : (
                      <Td>{booking.bookingDetailsList}</Td>
                    )}
                    <Td>{booking.currentStatus}</Td>
                    <Td>
                      <Button
                        bg="green.200"
                        isLoading={
                          bookingStatusUpdateMutation.isLoading &&
                          mutatingBookingId === booking.id
                        }
                        isDisabled={bookingStatusUpdateMutation.isLoading}
                        onClick={() => {
                          bookingStatusUpdateMutation.mutate({
                            ...booking,
                            currentStatus: "approved",
                          });
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        bg="red.500"
                        isLoading={
                          bookingStatusUpdateMutation.isLoading &&
                          mutatingBookingId === booking.id
                        }
                        isDisabled={bookingStatusUpdateMutation.isLoading}
                        onClick={() => {
                          bookingStatusUpdateMutation.mutate({
                            ...booking,
                            currentStatus: "declined",
                          });
                        }}
                      >
                        Decline
                      </Button>
                    </Td>
                  </Tr>
                ))}
                {/* Skeletons for loading state */}
                {isFetching && !fetchNew && (
                  <>
                    <Tr>
                      <Td colSpan="100%">
                        <Skeleton height="20px" />
                      </Td>
                    </Tr>
                    <Tr>
                      <Td colSpan="100%">
                        <Skeleton height="20px" />
                      </Td>
                    </Tr>
                  </>
                )}
              </Tbody>
            </Table>
          </TableContainer>
          {hasNextPage && (
            <Button
              isLoading={isFetching && !fetchNew}
              onClick={() => {
                setIsAppending(true);
                setCurrentPage(currentPage + 1);
              }}
            >
              Show more
            </Button>
          )}
        </>
      );
    } else {
      return (
        <>
          <Button isLoading={fetchNew} onClick={() => setFetchNew(true)}>
            Refetch
          </Button>
          <Accordion defaultIndex={[0]} allowMultiple>
            {fetchNew && <Skeleton height={"50px"}></Skeleton>}
            {bookingsData.map((booking, index) => (
              <AccordionItem key={booking.id}>
                <h2>
                  <AccordionButton>
                    <Box as="span" flex="1" textAlign="left">
                      <strong style={{ fontWeight: "bold" }}>
                        {booking.referenceNumber}
                      </strong>{" "}
                      <AdminBookingSourceCell booking={booking} compact />{" "}
                      {booking.client?.firstName} {booking.client?.lastName}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <Box as="span" flex="1" textAlign="left">
                    <Box mb={2}>
                      {t("adminBookedBy")}:{" "}
                      <AdminBookingSourceCell booking={booking} />
                    </Box>
                    <p>Telephone number: {booking.client?.telephoneNumber}</p>
                    <p>E-mail: {booking.client?.email}</p>
                    <p>Check-in date: {formatDateTimeCET(booking.checkInDate)}</p>
                    <p>Check-out date: {formatDateTimeCET(booking.checkOutDate)}</p>
                    <p>FullPrice: {booking.fullPrice}</p>
                    <p>
                      Reservation type:{" "}
                      {booking.accommodation
                        ? "stay"
                        : booking.car
                        ? "car"
                        : "tour"}
                    </p>
                    <p>
                      Driver inlcuded: {booking.driverIncluded ? "Yes" : "No"}
                    </p>

                    <p>Booking details:</p>
                    {booking.bookingType === "tour" ||
                    booking.bookingType === "stay" ? (
                      <p>
                        <BookingDetailsModal
                          tour={booking.tour}
                          accommodationUnits={booking.accommodationUnits}
                          accommodation={booking.accommodation}
                        />
                      </p>
                    ) : (
                      <p>{booking.bookingDetailsList}</p>
                    )}

                    <Button
                      size={"sm"}
                      bg="green.200"
                      isLoading={
                        bookingStatusUpdateMutation.isLoading &&
                        mutatingBookingId === booking.id
                      }
                      isDisabled={bookingStatusUpdateMutation.isLoading}
                      onClick={() => {
                        bookingStatusUpdateMutation.mutate({
                          ...booking,
                          currentStatus: "approved",
                        });
                      }}
                    >
                      Approve
                    </Button>
                    <Button
                      size={"sm"}
                      bg="red.500"
                      isLoading={
                        bookingStatusUpdateMutation.isLoading &&
                        mutatingBookingId === booking.id
                      }
                      isDisabled={bookingStatusUpdateMutation.isLoading}
                      onClick={() => {
                        bookingStatusUpdateMutation.mutate({
                          ...booking,
                          currentStatus: "declined",
                        });
                      }}
                    >
                      Decline
                    </Button>
                  </Box>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>

          {isFetching && !fetchNew && <Skeleton height={"50px"}></Skeleton>}

          {hasNextPage && (
            <Button
              isLoading={isFetching && !fetchNew}
              onClick={() => {
                setIsAppending(true);
                setCurrentPage(currentPage + 1);
              }}
            >
              Show more
            </Button>
          )}
        </>
      );
    }
  }
}

export default PendingBookings;
